/**
 * Carrito de invitado (sin cuenta), guardado en el navegador.
 *
 * Antes, agregar al carrito exigía tener sesión: quien hacía clic en
 * "Agregar" sin cuenta era mandado a la pantalla de login sin explicación.
 * Esto permite armar el carrito primero y pedir la cuenta hasta el checkout,
 * que es cuando de verdad hace falta (dirección de envío y pago).
 *
 * REGLA: aquí NO se guardan precios. Solo qué producto, cuánto y qué medida.
 * El precio siempre lo pone el servidor al pintar el carrito y al cobrar —
 * si el precio viviera aquí, cualquiera podría editarlo desde la consola del
 * navegador y pagar lo que quisiera.
 */
const KEY = 'carpepel_cart';
export const CART_EVENT = 'carpepel-cart-updated';

// Mismos topes que valida el backend (CartItemAdd: ge=1, le=500), para que
// un carrito manipulado a mano no llegue al servidor solo para ser rechazado.
const MIN_QTY = 1;
const MAX_QTY = 500;

const clampQty = (n) => {
    const q = Math.floor(Number(n));
    if (!Number.isFinite(q)) return MIN_QTY;
    return Math.min(MAX_QTY, Math.max(MIN_QTY, q));
};

/** Id de línea determinista: mismo criterio con el que el backend agrupa. */
export const lineId = (productId, selectedSize) => `${productId}::${selectedSize || ''}`;

/** Lee el carrito local. Nunca lanza: un localStorage corrupto o bloqueado
 *  (modo privado, cookies deshabilitadas) devuelve carrito vacío. */
export function getLocalCart() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((it) => it && typeof it.product_id === 'string' && it.product_id)
            .map((it) => ({
                id: lineId(it.product_id, it.selected_size),
                product_id: it.product_id,
                quantity: clampQty(it.quantity),
                selected_size: typeof it.selected_size === 'string' ? it.selected_size : '',
            }));
    } catch {
        return [];
    }
}

/**
 * Avisa a la interfaz (el contador del navbar) que el carrito cambió.
 *
 * Las funciones de este módulo ya lo hacen solas. Esto se exporta para el
 * carrito del SERVIDOR: con sesión iniciada, agregar no toca localStorage,
 * así que nadie disparaba el evento y el contador se quedaba con el número
 * viejo hasta cambiar de página.
 */
export function notifyCartChanged() {
    try {
        window.dispatchEvent(new Event(CART_EVENT));
    } catch {}
}

function writeLocalCart(items) {
    try {
        localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
        // Sin almacenamiento disponible el carrito simplemente no persiste;
        // no es motivo para romper la página.
    }
    notifyCartChanged();
    return items;
}

/** Agrega (o suma cantidad si ya existe esa combinación producto+medida). */
export function addToLocalCart(productId, quantity = 1, selectedSize = '') {
    const items = getLocalCart();
    const id = lineId(productId, selectedSize);
    const existing = items.find((it) => it.id === id);
    if (existing) {
        existing.quantity = clampQty(existing.quantity + quantity);
    } else {
        items.push({
            id,
            product_id: productId,
            quantity: clampQty(quantity),
            selected_size: selectedSize || '',
        });
    }
    return writeLocalCart(items);
}

export function updateLocalQuantity(id, quantity) {
    const items = getLocalCart();
    if (quantity < MIN_QTY) return removeLocalLine(id);
    const line = items.find((it) => it.id === id);
    if (line) line.quantity = clampQty(quantity);
    return writeLocalCart(items);
}

export function removeLocalLine(id) {
    return writeLocalCart(getLocalCart().filter((it) => it.id !== id));
}

export function clearLocalCart() {
    return writeLocalCart([]);
}

/** Número de líneas (mismo criterio que el `count` que devuelve el backend). */
export function getLocalCount() {
    return getLocalCart().length;
}

/**
 * Pasa el carrito de invitado a la cuenta recién iniciada y lo vacía de aquí.
 *
 * Sin esto, iniciar sesión con el carrito armado lo borraba de la vista: el
 * peor momento posible para perderlo, justo cuando ya decidieron comprar.
 * El backend suma cantidades si ya existe ese producto+medida, así que
 * fusionar es reenviar cada línea. Si una falla, se sigue con las demás en
 * vez de perder todo el carrito por un producto dado de baja.
 */
export async function mergeLocalCartIntoAccount(cartAPI) {
    const items = getLocalCart();
    if (items.length === 0) return 0;
    let merged = 0;
    for (const item of items) {
        try {
            await cartAPI.add({
                product_id: item.product_id,
                quantity: item.quantity,
                selected_size: item.selected_size,
            });
            merged += 1;
        } catch {}
    }
    clearLocalCart();
    return merged;
}
